import { useEffect, useRef } from 'react';

import { useRouter } from 'react-resource-router';

import { getDocument } from '@atlaskit/browser-apis';
import {
	BENCHMARK_REPORT_ROUTE_NAME,
	BENCHMARK_ROUTE_NAME,
	BENCHMARK_RUN_ROUTE_NAME,
	CLI_ROUTE_NAME,
	HOME_ROUTE_NAME,
	isMicrositeRouteName,
	GET_STARTED_ROUTE_NAME,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

/**
 * The browser `<title>` for each route.
 *
 * Home and CLI duplicate their dedicated document titles. The Benchmark titles
 * are application-only while those gated routes share the home document.
 * `src/__tests__/html-metadata.test.ts` guards the document-backed entries.
 */
export const ROUTE_TITLES: Record<MicrositeRouteName, string> = {
	[HOME_ROUTE_NAME]: 'Atlassian Teamwork Graph',
	[CLI_ROUTE_NAME]: 'Atlassian Teamwork Graph CLI',
	[BENCHMARK_ROUTE_NAME]: 'Atlassian Teamwork Graph Benchmark',
	[BENCHMARK_RUN_ROUTE_NAME]: 'Atlassian Teamwork Graph Benchmark',
	[BENCHMARK_REPORT_ROUTE_NAME]: 'Atlassian Teamwork Graph Benchmark',
	[GET_STARTED_ROUTE_NAME]: 'Atlassian Teamwork Graph Get Started',
};

/** Routes with no HTML document of their own, so the title is only ever set here. */
const APPLICATION_ONLY_ROUTE_NAMES: readonly string[] = [
	BENCHMARK_ROUTE_NAME,
	BENCHMARK_RUN_ROUTE_NAME,
	BENCHMARK_REPORT_ROUTE_NAME,
	GET_STARTED_ROUTE_NAME,
];

/**
 * Keeps the browser tab title in step with the route.
 *
 * Home and CLI serve route-specific HTML documents; Benchmark and Setup use the
 * home document while they remain feature-gated. SPA navigation also swaps the
 * route without fetching a destination document. This hook keeps the tab title
 * aligned in both cases.
 *
 * Only the title is synchronised. `canonical` and the `og:*`/`twitter:*` tags are
 * deliberately left alone: search engines and social unfurlers fetch each URL
 * directly and never see the result of an in-app navigation, so rewriting them
 * client-side would add moving parts without changing what any crawler reads.
 *
 * The first render is a no-op for routes with dedicated documents. The routes in
 * `APPLICATION_ONLY_ROUTE_NAMES` are updated immediately, because they
 * intentionally have no dedicated HTML entry.
 */
export const useDocumentTitleOnRouteChange = (): void => {
	const [{ route }] = useRouter();
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			if (!APPLICATION_ONLY_ROUTE_NAMES.includes(route?.name ?? '')) {
				return;
			}
		}

		// An unknown route resolves to the catch-all, which redirects to `/`. Leaving
		// the title alone means the tab keeps the previous route's title for the one
		// render that pass takes, rather than flashing something wrong.
		const routeName = route?.name;
		if (!isMicrositeRouteName(routeName)) {
			return;
		}

		const doc = getDocument();
		if (doc) {
			doc.title = ROUTE_TITLES[routeName];
		}
	}, [route?.name]);
};
